import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import Button from '~/components/core/Atomic/Buttons/Button';
import { MIXPANEL_EVENT_SOURCES } from '~/pocs/mixpanel';

import CardDialog from '../CardDialog';
import { useClaim } from '../ClaimContainer';
import MinimizedEmailDialogLauncher from '../communications/EmailCommunicationCard/MinimizedEmailDialogLauncher';
import SendEmailCommunicationCardContainer from '../communications/EmailCommunicationCard/SendEmailCommunicationCardContainer';
import { contactPopulate } from '../Contacts/ContactUtils';
import ContactTextFieldFormik from '../ContactTextFieldFormik';
import useFetchContact from '../hooks/useFetchContact';
import LoadingDialog from '../LoadingDialog';
import useDataFetcher from '../useDataFetcher';

function LitigationReferralAttorneySelectDialog(props) {
  const { onCancel, onSelectAttorney } = props;
  const { classes } = useStyles();
  return (
    <Formik
      initialValues={{
        attorney_contact: '',
      }}
      validationSchema={Yup.object().shape({
        attorney_contact: Yup.object()
          .required('Required')
          .test(
            'is-email-exists',
            'Contact must contain at least one email address',
            (contact) => contact && contact.emails && contact.emails.length > 0
          )
          .test(
            'is-emailing-allowed',
            'Sending emails to this contact was marked as not allowed',
            (contact) => contact && contact.is_emailing_allowed !== false
          ),
      })}
      onSubmit={(values) => onSelectAttorney(values['attorney_contact'])}
    >
      {(formikProps) => {
        const { isSubmitting, handleSubmit } = formikProps;
        return (
          <CardDialog isDialog title="Select Attorney" maxWidth="sm" onClose={onCancel} preventClose={isSubmitting}>
            <ContactTextFieldFormik
              id="attorney_contact"
              label="Attorney"
              className={classes.textField}
              acceptedRoles={['vendor']}
              fixedSearchResults
              fullWidth
            />
            <div className={classes.buttonsContainer}>
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                Select
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
}

LitigationReferralAttorneySelectDialog.propTypes = {
  onSelectAttorney: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * Hands the referral email to the minimized-dialog registry rather than rendering it here, so the
 * editor gets a minimize button. Every caller of this dialog is a claim screen, so the registry
 * `ClaimContextProvider` mounts is in scope and the editor still reads the same claim context.
 */
function SendLitigationReferralEmailDialog({ onClose, exposure, contact }) {
  return (
    <MinimizedEmailDialogLauncher
      barHeader={`Litigation Referral Email: ${contact.full_name}`}
      mixpanelSource={MIXPANEL_EVENT_SOURCES.LITIGATION_REFERRAL_EMAIL}
      dialogComponent={LitigationReferralEmailEditor}
      dialogProps={{ exposure, contact }}
      onLaunched={onClose}
    />
  );
}

SendLitigationReferralEmailDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  exposure: PropTypes.object,
  contact: PropTypes.object.isRequired,
};

function LitigationReferralEmailEditor({ onClose, exposure, contact, open, onMinimized, disableMinimized }) {
  const { claim, onAsyncClaimUpdate } = useClaim();

  const path = exposure
    ? `/api/v1/claims/${claim.id}/exposures/${exposure.id}/defense_litigation_referral_email_text`
    : `/api/v1/claims/${claim.id}/defense_litigation_referral_email_text`;
  const { isLoading, isError, data: emailTitleAndBody } = useDataFetcher(path);

  // In case the contact is not full and doesn't contain email
  const {
    isLoading: isLoadingContact,
    isError: isErrorContact,
    data: contactToSendEmail,
  } = useFetchContact(contact.id);

  if (isLoading || isError) {
    return <LoadingDialog track="Send litigation referral email" isError={isError} onClose={onClose} />;
  }

  if (isLoadingContact || isErrorContact) {
    return <LoadingDialog track="Send litigation referral email" isError={isErrorContact} onClose={onClose} />;
  }

  const { email_title: emailTitle, email_body: emailBody } = emailTitleAndBody;

  return (
    <SendEmailCommunicationCardContainer
      contact={contactPopulate(contactToSendEmail)}
      onClose={onClose}
      onSendEmail={async () => {
        await onAsyncClaimUpdate();
        onClose();
      }}
      emailTitle={emailTitle}
      emailText={emailBody}
      summary={`${contact.full_name} was assigned to handle litigation for this ${exposure ? 'exposure' : 'claim'}`}
      attachments={claim.documents
        .filter((document) => (exposure ? document.exposure_ids.includes(exposure.id) : true))
        .map((document) => ({ ...document, attachment_filename: document.document_name_with_ext }))}
      exposureIds={exposure ? [exposure.id] : [0]}
      open={open}
      onMinimized={onMinimized}
      disableMinimized={disableMinimized}
    />
  );
}

LitigationReferralEmailEditor.propTypes = {
  onClose: PropTypes.func.isRequired,
  exposure: PropTypes.object,
  contact: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onMinimized: PropTypes.func,
  disableMinimized: PropTypes.bool,
};

export default LitigationReferralAttorneySelectDialog;
export { SendLitigationReferralEmailDialog };
